import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Form, Modal, Space, Spin, Typography, message } from 'udesk-ui';
import DefaultLang from './components/DefaultLang';
import AdaptionEnabled from './components/AdaptionEnabled';
import DefaultSecondaryLang from './components/DefaultSecondaryLang';
import DefaultASRModelSelect from './components/DefaultASRModelSelect';
import Enabled from './components/Enabled';
import Rules from './components/Rules';
import { InformationOutlined } from '@udesk/icons';
import { getAsrFilterConfigMultilingualBySourceType } from 'src/api/asr-filter-config/multilingual/{sourceType}';
import {
    putAsrFilterConfigByIdMultilingual,
    deleteAsrFilterConfigByIdMultilingual,
} from 'src/api/asr-filter-config/{id}/multilingual';
import { postAsrFilterConfigMultilingual } from 'src/api/asr-filter-config/multilingual';
import { getAsrFilterConfigAsrLanguageSelf } from 'src/api/asr-filter-config/asr/language/self';
import { transform } from './utils';
import debounce from 'lodash/debounce';
import { AsrFilterConfig, AsrFilterConfigCreateRequest } from 'src/api/types';
import { SelectProps } from 'antd';

interface TemplateProps {
    inspectDataSource: number;
}

export interface FormModel {
    defaultLang?: AsrFilterConfig;
    defaultSecondaryLang?: AsrFilterConfig;
    defaultASRModel?: AsrFilterConfig;
    enabled?: AsrFilterConfig;
    rules?: AsrFilterConfig[];
    adaptionEnabled?: AsrFilterConfig;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { inspectDataSource } = props;

    const [languageOptions, setLanguageOptions] = React.useState<SelectProps<number>['options']>();

    useRequest(getAsrFilterConfigAsrLanguageSelf, {
        ready: inspectDataSource === Udesk.enums.inspectDataSources.voiceCall.id,
        onSuccess: (res) => {
            setLanguageOptions((res.data || []) as SelectProps<number>['options']);
        },
    });

    const { hasAdaption, hasASR } = useMemo(() => {
        const isVoiceCall = inspectDataSource === Udesk.enums.inspectDataSources.voiceCall.id;
        const isVideoCall = inspectDataSource === Udesk.enums.inspectDataSources.videoCall.id;

        return {
            /** 存在语言自适应 */
            hasAdaption: isVoiceCall,
            /** 存在ASR */
            hasASR: isVoiceCall || isVideoCall,
        };
    }, [inspectDataSource]);

    const [form] = Form.useForm<FormModel>();

    const { refresh, loading: getLoading } = useRequest(
        () =>
            getAsrFilterConfigMultilingualBySourceType({
                segments: { sourceType: inspectDataSource },
            }),
        {
            ready: !!inspectDataSource,
            onSuccess: (res) => {
                const values = transform(res.data);
                form.setFieldsValue(values);
            },
        }
    );

    const { run: onAdd, loading: onAddLoading } = useRequest(
        (data: AsrFilterConfigCreateRequest) => postAsrFilterConfigMultilingual(data),
        {
            manual: true,
            onSuccess: () => {
                message.success({
                    content:
                        /* 保存成功 */ UdeskLocales['current'].pages.qaLanguageSetting.template
                            .index.saveSuccessful,
                });
                refresh();
            },
        }
    );

    const { run: _onDelete, loading: onDeleteLoading } = useRequest(
        (id: number) => deleteAsrFilterConfigByIdMultilingual({ segments: { id } }),
        {
            manual: true,
            onSuccess: () => {
                message.success({
                    content:
                        /* 删除成功 */ UdeskLocales['current'].pages.qaLanguageSetting.template
                            .index.deleteSuccessful,
                });
                refresh();
            },
        }
    );

    const { run: onModify, loading: onModifyLoading } = useRequest(
        (id: number, data: AsrFilterConfigCreateRequest) =>
            putAsrFilterConfigByIdMultilingual(data, { segments: { id } }),
        {
            manual: true,
            onSuccess: () => {
                message.success({
                    content:
                        /* 修改成功 */ UdeskLocales['current'].pages.qaLanguageSetting.template
                            .index.modificationSucceeded,
                });
                refresh();
            },
        }
    );

    const onSave = useMemo(
        () =>
            debounce(
                (values?: AsrFilterConfig) => {
                    const defaultLangValue = form.getFieldValue('defaultLang');
                    const defaultLangFilter = form.getFieldValue('defaultLang')?.filter;
                    const adaptionFilter = form.getFieldValue('adaptionEnabled')?.filter;
                    const defaultSecondaryLangFilter =
                        form.getFieldValue('defaultSecondaryLang')?.filter;
                    const defaultLanguageType =
                        values?.filter?.languageType ?? defaultLangFilter?.languageType;
                    const autoDetection = values?.filter?.autoDetection;
                    const secondaryLanguageListRaw =
                        values?.filter?.multiLanguageTypeList ??
                        defaultSecondaryLangFilter?.multiLanguageTypeList ??
                        [];
                    const secondaryLanguageList = (
                        Array.isArray(secondaryLanguageListRaw) ? secondaryLanguageListRaw : []
                    ).filter((item): item is number => typeof item === 'number');
                    const currentAutoDetection = adaptionFilter?.autoDetection ?? defaultLangFilter?.autoDetection;
                    const isAdaptionEnabled = hasAdaption && currentAutoDetection === 1;
                    // 确定要保存的autoDetection值，优先使用传入的值，否则使用当前值
                    const autoDetectionToSave = autoDetection !== undefined ? autoDetection : currentAutoDetection;

                    // 副语言输入仅保留用户选择的副语言，提交时也保持纯副语言，不混入主语言。
                    const normalizedSecondaryLanguageList = Array.from(
                        new Set(
                            secondaryLanguageList.filter((item) => item !== defaultLanguageType)
                        )
                    );

                    if (isAdaptionEnabled) {
                        if (normalizedSecondaryLanguageList.length < 1) {
                            message.error(
                                /* 请选择副语言 */ UdeskLocales['current'].pages.qaLanguageSetting
                                    .template.index.pleaseSelectASecondaryLanguage
                            );
                            return;
                        }
                    }

                    const mergedMultiLanguageTypeList = isAdaptionEnabled
                        ? normalizedSecondaryLanguageList
                        : secondaryLanguageList;

                    const filter: any = {
                        ...values?.filter,
                        languageType: defaultLanguageType,
                        multiLanguageTypeList: mergedMultiLanguageTypeList,
                    };
                    if (autoDetectionToSave !== undefined) {
                        filter.autoDetection = autoDetectionToSave;
                    }
                    const params = {
                        activeFlag: values?.activeFlag,
                        filter,
                        secondaryType: values?.secondaryType,
                        sourceType: values?.sourceType,
                        type: values?.type,
                    };

                    // LANG 配置应该始终落在同一条记录上，避免切换主语言后副语言保存被误新增。
                    const shouldUseDefaultLangId = values?.secondaryType === 3;
                    const targetId = values?.id ?? (shouldUseDefaultLangId ? defaultLangValue?.id : undefined);

                    if (targetId) {
                        onModify(targetId, params);
                    } else {
                        onAdd(params);
                    }
                },
                300,
                {
                    leading: true,
                    tailing: true,
                }
            ),
        [form, hasAdaption, onAdd, onModify]
    );

    const onDefaultLangChange = useCallback(
        (values?: AsrFilterConfig) => {
            // 获取当前的语言自适应开关状态
            const adaptionFilter = form.getFieldValue('adaptionEnabled')?.filter;
            const currentAutoDetection = adaptionFilter?.autoDetection;
            
            // 置空表单对应值
            form.setFieldsValue({
                defaultSecondaryLang: undefined,
                defaultASRModel: undefined,
            });
            // 保存新的值，包含当前的语言自适应开关状态
            onSave({
                ...values,
                filter: {
                    ...values?.filter,
                    multiLanguageTypeList: undefined,
                    asrEngineType: undefined,
                    autoDetection: currentAutoDetection,
                },
            });
        },
        [form, onSave]
    );

    const onAdaptionEnabledChange = useCallback(
        (values?: AsrFilterConfig) => {
            // 立即更新表单状态，确保后续操作能获取到最新的开关状态
            form.setFieldsValue({
                adaptionEnabled: values,
            });
            // 同时更新defaultLang和defaultSecondaryLang字段中的autoDetection，确保数据同步
            const defaultLang = form.getFieldValue('defaultLang');
            const defaultSecondaryLang = form.getFieldValue('defaultSecondaryLang');
            
            if (values?.filter?.autoDetection !== undefined) {
                if (defaultLang) {
                    form.setFieldsValue({
                        defaultLang: {
                            ...defaultLang,
                            filter: {
                                ...defaultLang?.filter,
                                autoDetection: values.filter.autoDetection,
                            },
                        },
                    });
                }
                if (defaultSecondaryLang) {
                    form.setFieldsValue({
                        defaultSecondaryLang: {
                            ...defaultSecondaryLang,
                            filter: {
                                ...defaultSecondaryLang?.filter,
                                autoDetection: values.filter.autoDetection,
                            },
                        },
                    });
                }
            }
            // 保存新的值
            onSave(values);
        },
        [form, onSave]
    );

    const onDelete = (id?: number) => {
        if (id) {
            Modal.confirm({
                title: /* 确定删除吗？ */ UdeskLocales['current'].pages.qaLanguageSetting.template
                    .index.areYouSureToDeleteIt,
                onOk: () => {
                    _onDelete(id);
                },
            });
        } else {
            refresh();
        }
    };

    return (
        <Form<FormModel> form={form} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
            <Spin spinning={getLoading || onModifyLoading || onAddLoading || onDeleteLoading}>
                <Form.Item
                    label={
                        /* 默认语言 */ UdeskLocales['current'].pages.qaLanguageSetting.template
                            .index.defaultLanguage
                    }
                    name="defaultLang"
                >
                    <DefaultLang
                        sourceType={inspectDataSource}
                        onChange={onDefaultLangChange}
                        options={languageOptions}
                    />
                </Form.Item>

                {hasAdaption ? (
                    <>
                        <Form.Item
                            label={
                                /* 语言自适应 */ UdeskLocales['current'].pages.qaLanguageSetting
                                    .template.index.languageAdaptive
                            }
                        >
                            <Space>
                                <Form.Item name="adaptionEnabled" noStyle>
                                    <AdaptionEnabled
                                        sourceType={inspectDataSource}
                                        onChange={onAdaptionEnabledChange}
                                    />
                                </Form.Item>

                                <Space>
                                    <InformationOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />
                                    <Typography.Text style={{ color: 'rgba(0, 0, 0, 0.25)' }}>
                                        {/* 开启后可为一条语音设置多种转写语言，模型自动识别并选择语言进行转写。语言自适应仅在使用谷歌、微软、阿里paraformer-v2时生效，其他ASR引擎不生效。 */}
                                        {
                                            UdeskLocales['current'].pages.qaLanguageSetting.template
                                                .index
                                                .whenEnabledYouCanSetMultipleTranscriptionLanguagesForAVoiceTheModelAutomaticallyRecognizesAndSelectsALanguageForTranscriptionLanguageAdaptationOnlyTakesEffectWhenUsingGoogleMicrosoftAndAliParaformervOtherASREnginesDoNotTakeEffect
                                        }
                                    </Typography.Text>
                                </Space>
                            </Space>
                        </Form.Item>

                        <Form.Item<FormModel> noStyle shouldUpdate>
                            {({ getFieldsValue }) => {
                                const adaptionEnabled =
                                    getFieldsValue().adaptionEnabled?.filter?.autoDetection === 1;
                                const languageType =
                                    getFieldsValue().defaultLang?.filter?.languageType;

                                return adaptionEnabled ? (
                                    <Form.Item
                                        label={
                                            /* 默认副语言 */ UdeskLocales['current'].pages
                                                .qaLanguageSetting.template.index.defaultSublanguage
                                        }
                                        name="defaultSecondaryLang"
                                    >
                                        <DefaultSecondaryLang
                                            sourceType={inspectDataSource}
                                            onChange={onSave}
                                            disabledList={languageType ? [languageType] : []}
                                            options={languageOptions}
                                        />
                                    </Form.Item>
                                ) : null;
                            }}
                        </Form.Item>
                    </>
                ) : null}

                {hasASR ? (
                    <Form.Item noStyle shouldUpdate>
                        {() => {
                            const languageType =
                                form.getFieldsValue().defaultLang?.filter?.languageType;

                            return (
                                <Form.Item
                                    label={
                                        /* 默认ASR模型 */ UdeskLocales['current'].pages
                                            .qaLanguageSetting.template.index.defaultASRModel
                                    }
                                    name="defaultASRModel"
                                >
                                    <DefaultASRModelSelect
                                        languageType={languageType}
                                        sourceType={inspectDataSource}
                                        onChange={onSave}
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                ) : null}

                <Form.Item
                    label={
                        /* 多语言设置 */ UdeskLocales['current'].pages.qaLanguageSetting.template
                            .index.multilanguageSettings
                    }
                >
                    <Space>
                        <Form.Item name="enabled" noStyle>
                            <Enabled sourceType={inspectDataSource} onChange={onSave} />
                        </Form.Item>

                        <Space>
                            <InformationOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />
                            <Typography.Text style={{ color: 'rgba(0, 0, 0, 0.25)' }}>
                                {/* 为不同数据指定不同的语言 */}
                                {
                                    UdeskLocales['current'].pages.qaLanguageSetting.template.index
                                        .specifyDifferentLanguagesForDifferentData
                                }
                            </Typography.Text>
                        </Space>
                    </Space>

                    <Form.Item<FormModel> noStyle shouldUpdate>
                        {() => {
                            const { defaultLang, enabled } = form.getFieldsValue();
                            const show = enabled?.activeFlag === 1;
                            const languageType = defaultLang?.filter?.languageType;

                            return show ? (
                                <Form.Item name="rules" noStyle>
                                    <Rules
                                        sourceType={inspectDataSource}
                                        onSave={onSave}
                                        onDelete={onDelete}
                                        disabledList={languageType ? [languageType] : []}
                                        hasAdaption={hasAdaption}
                                        hasASR={hasASR}
                                        options={languageOptions}
                                    />
                                </Form.Item>
                            ) : null;
                        }}
                    </Form.Item>
                </Form.Item>
            </Spin>
        </Form>
    );
});

export default Template;
